import type { Collection } from "types/collection";
import type { DashboardMetricsData, DashboardMetric } from "types/dashboardMetrics";

export interface QuadrantDataPoint {
  projectId?: string;
  projectName: string;
  x: number;
  y: number;
  companyLogoUrl?: string;
  color: string;
}

export interface DirectQuadrantInput {
  investmentScoreAnalytical?: { value: string };
  investmentScoreSentiment?: { value: string };
  projectName?: string;
  id?: string;
  companyLogoUrl?: string;
  color?: string;
}

export interface DashboardMetricsOptions {
  dashboardMetrics?: DashboardMetricsData | DashboardMetric[] | undefined;
  collectionName?: string;
  collectionId?: string;
  companyLogoUrl?: string;
}

export const generateQuadrantData = (
  multipleProjectData?: Collection[] | undefined,
  singleProjectData?: DirectQuadrantInput | DirectQuadrantInput[],
  options?: DashboardMetricsOptions
): QuadrantDataPoint[] => {
  // Handle dashboard metrics case first (highest priority)
  if (options?.dashboardMetrics) {
    const dashboardData = processDashboardMetrics(
      options.dashboardMetrics,
      options.collectionName,
      options.collectionId,
      options.companyLogoUrl
    );
    if (dashboardData) {
      return [dashboardData];
    }
  }

  // Handle direct input case
  if (singleProjectData) {
    const inputs = Array.isArray(singleProjectData) ? singleProjectData : [singleProjectData];
    return inputs
      .map((input) => {
        const { investmentScoreAnalytical, investmentScoreSentiment, projectName, id, companyLogoUrl } = input || {};

        return processQuadrantDataPoint(investmentScoreAnalytical, investmentScoreSentiment, projectName, id, companyLogoUrl);
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  // If we reach here, singleProjectData is not provided
  // Check if multipleProjectData is provided
  if (multipleProjectData) {
    return multipleProjectData
      .map((collection) => {
        const { investmentScoreAnalytical, investmentScoreSentiment, name, id } = collection || {};

        return processQuadrantDataPoint(investmentScoreAnalytical, investmentScoreSentiment, name, id);
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  // Neither singleProjectData nor multipleProjectData is provided
  return [];
};

// Helper function to process a single data point
const processQuadrantDataPoint = (
  investmentScoreAnalytical?: { value: string },
  investmentScoreSentiment?: { value: string },
  name?: string,
  id?: string,
  companyLogoUrl?: string,
  color?: string
): QuadrantDataPoint | null => {
  const analyticalValue = investmentScoreAnalytical?.value ?? "";
  const sentimentValue = investmentScoreSentiment?.value ?? "";

  const regexInvestmentScoreAnalytical = analyticalValue.match(/\d+(\.\d+)?/g);
  const regexInvestmentScoreSentiment = sentimentValue.match(/\d+(\.\d+)?/g);

  const x = Number(regexInvestmentScoreAnalytical?.[0] ?? 0);
  const y = Number(regexInvestmentScoreSentiment?.[0] ?? 0);

  if (x === 0 || y === 0) return null;

  return {
    projectId: id || "",
    projectName: name || "",
    x: Math.min(Math.max(x, 0), 100),
    y: Math.min(Math.max(y, 0), 100),
    companyLogoUrl: companyLogoUrl || "",
    color: color || "gray.500",
  };
};

// Helper function to process dashboard metrics data
const processDashboardMetrics = (
  dashboardMetrics: DashboardMetricsData | DashboardMetric[],
  collectionName?: string,
  collectionId?: string,
  companyLogoUrl?: string
): QuadrantDataPoint | null => {
  let metrics: DashboardMetric[] = [];

  // Handle different dashboard metrics data structures
  if (Array.isArray(dashboardMetrics)) {
    // Check if it's DashboardMetricsData (array of groups) or DashboardMetric[] (array of metrics)
    if (dashboardMetrics.length > 0 && "group" in dashboardMetrics[0]) {
      // It's DashboardMetricsData - find the quadrant group
      const quadrantGroup = (dashboardMetrics as DashboardMetricsData).find((group) => group.group === "pi_quadrant_index");
      metrics = quadrantGroup?.metrics || [];
    } else {
      // It's DashboardMetric[]
      metrics = dashboardMetrics as DashboardMetric[];
    }
  }

  if (metrics.length === 0) return null;

  // Find fundamentals and sentiment metrics
  const fundamentalsMetric = metrics.find((metric) => metric.subgroup === "fundamentals");
  const sentimentMetric = metrics.find((metric) => metric.subgroup === "sentiment");

  if (!fundamentalsMetric || !sentimentMetric) return null;

  // Extract the first data value from each metric and multiply by 10
  const x = (fundamentalsMetric.data?.[0] ?? 0) * 10;
  const y = (sentimentMetric.data?.[0] ?? 0) * 10;

  // Allow 0 values for dashboard metrics, only return null if both metrics are missing
  if (!fundamentalsMetric.data && !sentimentMetric.data) return null;

  return {
    projectId: collectionId || "",
    projectName: collectionName || "Project",
    companyLogoUrl: companyLogoUrl || "",
    x: Math.min(Math.max(x, 0), 100),
    y: Math.min(Math.max(y, 0), 100),
    color: "gray.500",
  };
};
